import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { INTRO_STEP_DEFAULT_DEFINITION, REVIEW_STEP_DEFAULT_DEFINITION } from '../lib/constants';

import useStepper from './useStepper';

export const useStepperOrchestration = ({
  wizardState,
  wizardKind,
  hiddenSubstepCodes,
  navigateToCode,
  onNavigateToCodeHandled
}) => {
  const { instance = {}, steps: initialSteps = [], phases = null } = wizardState;
  const location = useLocation();

  const baseUrl = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const onboardingIndex = pathParts.indexOf('onboarding');
    if (onboardingIndex > -1) {
      return pathParts.slice(0, onboardingIndex + 1).join('/');
    }
    return location.pathname;
  }, [location.pathname]);

  const stepsWithIntroAndReview = useMemo(
    () => [INTRO_STEP_DEFAULT_DEFINITION, ...initialSteps, REVIEW_STEP_DEFAULT_DEFINITION],
    [initialSteps]
  );

  const {
    stepsState,
    activeStep,
    activeSubstep,
    activateStep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    navigateTo,
    goNext,
    goPrev
  } = useStepper(stepsWithIntroAndReview, { syncWithUrl: true, baseUrl });

  // Guard against navigateTo identity changing mid-flight before the parent
  // has cleared navigateToCode — without this, the effect can re-fire and
  // dispatch the same code twice.
  const handledCodeRef = useRef(null);
  useEffect(() => {
    if (!navigateToCode) {
      handledCodeRef.current = null;
      return;
    }
    if (handledCodeRef.current === navigateToCode) return;
    handledCodeRef.current = navigateToCode;
    navigateTo(navigateToCode);
    onNavigateToCodeHandled?.();
  }, [navigateToCode, navigateTo, onNavigateToCodeHandled]);

  const activateStepWithHiddenCheck = useCallback(
    (stepCode) => {
      if (hiddenSubstepCodes.size === 0) {
        activateStep(stepCode);
        return;
      }
      const step = stepsState.find((s) => s.code === stepCode);
      const firstVisible = step?.substeps?.find((sub) => !hiddenSubstepCodes.has(sub.code));
      if (firstVisible && hiddenSubstepCodes.has(step?.substeps?.[0]?.code)) {
        navigateToSubstep(firstVisible.code);
      } else {
        activateStep(stepCode);
      }
    },
    [activateStep, navigateToSubstep, stepsState, hiddenSubstepCodes]
  );

  const orchestratorData = useMemo(
    () => ({
      instanceId: instance?.id,
      wizardKind,
      practiceId: instance?.instance_id,
      stepCode: activeStep?.code,
      substepCode: activeSubstep?.code,
      stepId: activeStep?.step_id ?? activeStep?.id,
      substepId: activeSubstep?.substep_id ?? activeSubstep?.id,
      stepName: activeStep?.name,
      substepName: activeSubstep?.name,
      substepStatus: activeSubstep?.status,
      handlerStatus: activeSubstep?.status
    }),
    [instance?.id, instance?.instance_id, wizardKind, activeStep, activeSubstep]
  );

  const progressValue = useMemo(() => {
    const n = Number(instance?.progress);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0;
  }, [instance?.progress]);

  return {
    instance,
    phases,
    stepsState,
    activeStep,
    activeSubstep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    goNext,
    goPrev,
    activateStepWithHiddenCheck,
    orchestratorData,
    progressValue
  };
};
